import { Cancel, CheckCircle, ContactMail, OpenInNew } from '@mui/icons-material';
import { Alert, AlertTitle, alpha, Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { ContentLabelAndValue } from 'src/shared/components/organisms/contentLabelAndValue';
import { useEffect, useState } from 'react';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { getUnsubscribeToken } from 'src/shared/services/memberService';
import { MemberRecentChangeInfo } from 'src/modules/dashboard/components/members/memberRecentChangeInfo';
import { displayAutogiroStatus } from 'src/shared/models/enums/autogiroPeriodicity.enum';
import { displayStripeStatus, displayStripePeriodicityOnly } from 'src/shared/models/enums/stripePeriodicity.enum';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Member } from '../models/member';
import { MemberFamilyMembers } from './memberFamilyMembers';
import { MemberDetailExternalLinks } from './memberDetailExternalLinks';
import { MemberMeFamilyMembers } from './memberMeFamilyMembers';
import { MemberMeMemberCardModal } from './memberMeMemberCardModal';
import { MemberPaymentMethod } from './memberPaymentMethod';
import { AutogiroMandateInfoSection } from './autogiroMandateInfoSection';
import { MemberChangeExpireDateModal } from './memberChangeExpireDateModal';

type MembershipPartialProps = {
  otherContactMode: boolean;
  member: Member;
  showEditButtons?: boolean | undefined;
  refetchMemberCallback: () => void;
  refetchMemberMeCallback?: () => void;
};

export const MembershipPartial = (props: MembershipPartialProps) => {
  const { otherContactMode, member, showEditButtons = true, refetchMemberCallback, refetchMemberMeCallback = undefined } = props;
  const { t } = useTranslation(['member', 'common']);
  const { currentUser, currentMember } = useGlobals();

  const [openMemberCard, setOpenMemberCard] = useState<boolean>(false);
  const [openChangeExpireDateModal, setOpenChangeExpireDateModal] = useState<boolean>(false);
  const [unsubscribeUrl, setUnsubscribeUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!member?.member_id) return;
    getUnsubscribeToken(member.member_id)
      .then((res) => setUnsubscribeUrl(res.data.url))
      .catch(() => undefined);
  }, [member?.member_id]);

  const disableEditExpireButton = !!member?.currentInvoices?.length;
  const paidMember = (member && member.status && member.status.id === MemberStatusEnum.Paid) ?? false;
  const useDigitalMembercardUser =
    currentUser?.mainSettings.find((setting) => setting.key === MainSettingsEnum.UseDigitalMembercard)?.value === 'True';
  const useDigitalMembercardMember =
    currentMember?.mainSettings.find((setting) => setting.key === MainSettingsEnum.UseDigitalMembercard)?.value === 'True';

  const displayButton = currentUser ? useDigitalMembercardUser : useDigitalMembercardMember;

  const getPaymentStatus = () => {
    if (member.autogiroStatus) return displayAutogiroStatus(member.autogiroStatus, t);
    if (member.stripeStatus) return displayStripeStatus(member.stripeStatus, t);
    if (member.swishStatus) return `${t('member:swishRecurring')} (${displayStripePeriodicityOnly(member.swishStatus, t)})`;
    if (member.eInvoiceAddress) return t('member:eInvoice');
    return t('member:noAutogiro');
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs={12}>
          <ContentSection
            header={
              otherContactMode
                ? getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.label'))
                : t('member:memberDetails.membership')
            }
            button={
              displayButton && paidMember ? (
                <Button type='button' variant='outlined' size='small' endIcon={<ContactMail />} onClick={() => setOpenMemberCard(true)}>
                  {t('common:button.openMemberCard')}
                </Button>
              ) : undefined
            }
          >
            <ContentLabelAndValue label={t('member:tableHeaders.createDate')} value={DateHelper.getDate(member.createDate)} />
            <ContentLabelAndValue
              label={otherContactMode ? t('member:tableHeaders.nr') : t('member:tableHeaders.member_id')}
              value={member.memberNr?.toString()}
            />
            <ContentLabelAndValue
              label={getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, t('member:tableHeaders.district'))}
              value={(member.district && member.district.countyAss) || ''}
            />
            <ContentLabelAndValue
              label={getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, t('member:tableHeaders.association'))}
              value={(member.association && member.association.localAss) || ''}
            />
            <ContentLabelAndValue label={t('member:tableHeaders.admissionDate')} value={DateHelper.getDate(member.admDate)} />
            <ContentLabelAndValue label={t('member:tableHeaders.resDate')} value={DateHelper.getDate(member.resDate)} />

            <Grid container direction='row' alignItems='center' justifyContent='space-between'>
              <Grid item xs={8}>
                <ContentLabelAndValue label={t('member:tableHeaders.expireDate')} value={DateHelper.getDate(member.expireDate)} />
              </Grid>
              <Grid item xs={4} textAlign='right' sx={{ mt: 2 }}>
                {currentUser && !otherContactMode && (
                  // Visa inte för inloggad medlem - endast användare
                  <Tooltip arrow placement='left' title={disableEditExpireButton ? t('member:memberDetails.cannotChangeExpireDate') : ''}>
                    <span>
                      <Button
                        disabled={disableEditExpireButton}
                        variant='outlined'
                        size='small'
                        disableElevation
                        onClick={() => setOpenChangeExpireDateModal(true)}
                      >
                        {t('common:button.change')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Grid>
            </Grid>

            <ContentLabelAndValue label={t('member:tableHeaders.resCause')} value={member.resCause} />
            {!otherContactMode && (
              <Grid container mb={1} direction='row' alignItems='center' justifyContent='space-between'>
                <ContentLabelAndValue label={t('member:tableHeaders.autogiroStatus')} value={getPaymentStatus()} />
                {currentUser && member.memberNr && (
                  <Grid item>
                    <MemberPaymentMethod member={member} refetchMemberCallback={refetchMemberCallback} />
                  </Grid>
                )}
                {member.autogiroMandate && <AutogiroMandateInfoSection member={member} refetchMemberCallback={refetchMemberCallback} />}
              </Grid>
            )}
            {otherContactMode && currentUser && (
              <Grid container mb={1} direction='row' alignItems='center' justifyContent='space-between'>
                <ContentLabelAndValue
                  label={t('member:tableHeaders.autogiroStatus')}
                  value={member.eInvoiceAddress ? t('member:eInvoice') : t('member:noAutogiro')}
                />
                <Grid item>
                  <MemberPaymentMethod member={member} refetchMemberCallback={refetchMemberCallback} otherContactMode />
                </Grid>
              </Grid>
            )}
            {member.eInvoiceAddress && !member.eInvoiceAddress.receiver && (
              <Grid container pl={1} mt={1}>
                <Grid item xs={12}>
                  <Alert severity='error' icon={false}>
                    <AlertTitle sx={{ fontWeight: 600 }}>{t('member:eInvoiceMissingReceiver.title')}</AlertTitle>
                    {t('member:eInvoiceMissingReceiver.description')}
                  </Alert>
                </Grid>
              </Grid>
            )}
            {member.eInvoiceAddress?.receiver && (
              <Grid container pl={1} mt={1}>
                <Grid item xs={12} sx={{ backgroundColor: (thm) => alpha(thm.palette.grey[200], 0.35), borderRadius: 1, px: 1.5, py: 1 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {t('member:eInvoiceReceiver')}
                  </Typography>
                  <Typography variant='body2'>{member.eInvoiceAddress.receiver.name}</Typography>
                  {member.eInvoiceAddress.receiver.cin && (
                    <Typography variant='body2'>
                      {t('member:eInvoiceTooltip.cin')}: {member.eInvoiceAddress.receiver.cin}
                    </Typography>
                  )}
                  {member.eInvoiceAddress.receiver.gln && <Typography variant='body2'>GLN: {member.eInvoiceAddress.receiver.gln}</Typography>}
                  {member.eInvoiceAddress.buyerReference && (
                    <Typography variant='body2'>
                      {t('member:eInvoiceTooltip.reference')}: {member.eInvoiceAddress.buyerReference}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            )}
          </ContentSection>
        </Grid>

        {!otherContactMode && currentMember && currentMember.enableAddFamily && member.enableAddFamily && refetchMemberMeCallback && (
          <MemberMeFamilyMembers showEditButtons={showEditButtons} member={member} refetchMemberCallback={refetchMemberMeCallback} />
        )}
        {!otherContactMode && !currentMember && member.enableAddFamily && (
          <MemberFamilyMembers showEditButtons={showEditButtons} member={member} refetchMemberCallback={refetchMemberCallback} />
        )}

        {(member.newsletterEmailUnsubscribed !== undefined || member.newsletterSMSUnsubscribed !== undefined) && (
          <Grid item xs={12}>
            <ContentSection
              header={t('member:communication.header')}
              button={
                unsubscribeUrl ? (
                  <Button
                    type='button'
                    variant='outlined'
                    size='small'
                    href={currentUser ? `${unsubscribeUrl}?userId=${currentUser.user_id}` : unsubscribeUrl}
                    target='_blank'
                    rel='noreferrer'
                    endIcon={<OpenInNew />}
                  >
                    {t('member:communication.manage')}
                  </Button>
                ) : undefined
              }
            >
              {member.newsletterEmailUnsubscribed !== undefined && (
                <Box display='flex' flexDirection='column' pl={1} pb={1}>
                  <Typography variant='body1' fontWeight={(thm) => thm.typography.fontWeightBold}>
                    {t('member:communication.allowEmail')}:
                  </Typography>
                  <Box display='flex' alignItems='center' gap={0.5}>
                    {member.newsletterEmailUnsubscribed === true ? (
                      <Cancel color='error' fontSize='small' />
                    ) : (
                      <CheckCircle color='success' fontSize='small' />
                    )}
                    <Typography variant='body1'>
                      {member.newsletterEmailUnsubscribed === true ? t('common:button.no') : t('common:button.yes')}
                    </Typography>
                  </Box>
                </Box>
              )}
              {member.newsletterSMSUnsubscribed !== undefined && (
                <Box display='flex' flexDirection='column' pl={1} pb={1}>
                  <Typography variant='body1' fontWeight={(thm) => thm.typography.fontWeightBold}>
                    {t('member:communication.allowSMS')}:
                  </Typography>
                  <Box display='flex' alignItems='center' gap={0.5}>
                    {member.newsletterSMSUnsubscribed === true ? (
                      <Cancel color='error' fontSize='small' />
                    ) : (
                      <CheckCircle color='success' fontSize='small' />
                    )}
                    <Typography variant='body1'>
                      {member.newsletterSMSUnsubscribed === true ? t('common:button.no') : t('common:button.yes')}
                    </Typography>
                  </Box>
                </Box>
              )}
            </ContentSection>
          </Grid>
        )}

        {!otherContactMode && <MemberDetailExternalLinks member={member} />}
        {!currentMember && <MemberRecentChangeInfo member={member} />}

        {member && member.member_id && openMemberCard && (
          <MemberMeMemberCardModal modalOpen={openMemberCard} onClose={() => setOpenMemberCard(false)} memberId={member.member_id} />
        )}

        {openChangeExpireDateModal && (
          <MemberChangeExpireDateModal
            key={member.member_id}
            open={openChangeExpireDateModal}
            setOpen={setOpenChangeExpireDateModal}
            member={member}
            refetchMemberCallback={refetchMemberCallback}
          />
        )}
      </GridWrapper>
    </>
  );
};
